import { t } from "i18next";
import { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import DBModal from "../../../../../component/global/DBModal";
import DBButton from "../../../../../component/global/DBButton";
import DBFieldBlock from "../../../../../component/global/DBFieldBlock";
import DBSelect from "../../../../../component/global/DBSelect";
import DBTextArea from "../../../../../component/global/DBTextArea";
import { ToastContext } from "../../../../../component/global/ToastContext";
import { useRequest } from "../../../../../hooks/helperData/useRequest";
import { FEEDBACK_PRINT_POSTER_AI } from "../../../../../helperData/ApiAddress";
import {
    AiCoinHint,
    isInsufficientCoins,
    useAiCoinGate,
} from "../../../../../features/ai-billing/AiCoinGate";
import {
    DEFAULT_PRINT_POSTER_AI_MODEL,
    PAPER_SIZES,
    POSTER_FOCUS_ELEMENTS,
    POSTER_TONES,
    PRINT_POSTER_AI_MODELS,
    SAMPLE_ITEM_NAME,
} from "./constants";
import PrintPosterPreview from "./PrintPosterPreview";

const defaultAnswers = (paperSize, focusElement) => ({
    industry: "",
    audience: "",
    tone: "hospitality",
    paper_size: paperSize || "a5",
    focus_element: focusElement || "qr",
    notes: "",
});

function ModelField({ value, onChange }) {
    return (
        <DBFieldBlock title={t("print.poster.ai.model")}>
            <DBSelect
                value={value}
                options={PRINT_POSTER_AI_MODELS.map((model) => ({
                    value: model.id,
                    label: t(model.labelKey),
                }))}
                onChange={onChange}
                style={{ width: "100%" }}
                aria-label={t("print.poster.ai.model")}
            />
        </DBFieldBlock>
    );
}

export default function AIPrintPosterModal({
    show,
    onHide,
    paperSize,
    focusElement,
    company,
    existingPoster,
    onGenerated,
}) {
    const toast = useContext(ToastContext);
    const [phase, setPhase] = useState(existingPoster ? "choice" : "form");
    const [answers, setAnswers] = useState(
        defaultAnswers(paperSize, focusElement)
    );
    const [selectedModel, setSelectedModel] = useState(
        DEFAULT_PRINT_POSTER_AI_MODEL
    );
    const [error, setError] = useState("");
    const [draftPoster, setDraftPoster] = useState(null);
    const [revisionPrompt, setRevisionPrompt] = useState("");
    const [generatePoster, generateLoading] = useRequest(
        FEEDBACK_PRINT_POSTER_AI,
        "POST"
    );
    const wasOpenRef = useRef(false);
    const {
        confirmAndRun,
        openFromError,
        gate,
        isOpen: coinGateOpen,
    } = useAiCoinGate();

    useEffect(() => {
        if (show && !wasOpenRef.current) {
            setAnswers(defaultAnswers(paperSize, focusElement));
            setSelectedModel(DEFAULT_PRINT_POSTER_AI_MODEL);
            setError("");
            setRevisionPrompt("");
            setDraftPoster(null);
            setPhase(existingPoster ? "choice" : "form");
        }
        wasOpenRef.current = Boolean(show);
    }, [show, paperSize, focusElement, existingPoster]);

    const toneOptions = POSTER_TONES.map((tone) => ({
        value: tone,
        label: t(`print.poster.tone.${tone}`),
    }));
    const paperOptions = Object.keys(PAPER_SIZES).map((id) => ({
        value: id,
        label: t(`print.poster.paper.${id}`),
    }));
    const isPreview = phase === "preview" && Boolean(draftPoster);
    const isChoice = phase === "choice";

    const buildPayload = (extra = {}) => ({
        industry: answers.industry.trim(),
        audience: answers.audience.trim(),
        tone: answers.tone,
        paper_size: answers.paper_size,
        focus_element: answers.focus_element || "qr",
        notes: answers.notes.trim(),
        model: selectedModel,
        ...extra,
    });

    const requestPoster = (payload, { onSuccess } = {}) => {
        confirmAndRun("print_poster", () => {
            generatePoster(payload)
                .then((result) => {
                    if (!result?.poster) {
                        toast(t("print.poster.ai.error"), "danger");
                        return;
                    }
                    setDraftPoster(result.poster);
                    setPhase("preview");
                    setError("");
                    onSuccess?.(result.poster);
                })
                .catch((requestError) => {
                    if (isInsufficientCoins(requestError)) {
                        openFromError(requestError);
                        return;
                    }
                    toast(
                        requestError?.response?.data?.detail ||
                            t("print.poster.ai.error"),
                        "danger"
                    );
                });
        });
    };

    const generate = () => {
        if (!answers.industry.trim()) {
            setError(t("print.poster.ai.required"));
            return;
        }
        setError("");
        requestPoster(buildPayload());
    };

    const revise = () => {
        if (!revisionPrompt.trim()) {
            setError(t("print.poster.ai.revise.required"));
            return;
        }
        if (!draftPoster) return;
        setError("");
        requestPoster(
            buildPayload({
                revision_prompt: revisionPrompt.trim(),
                current_poster: draftPoster,
            }),
            {
                onSuccess: () => setRevisionPrompt(""),
            }
        );
    };

    const startReviseExisting = () => {
        if (!existingPoster) return;
        setDraftPoster(existingPoster);
        setAnswers({
            ...defaultAnswers(
                existingPoster.paper_size || paperSize,
                existingPoster.focus_element || focusElement
            ),
        });
        setPhase("preview");
        setError("");
        setRevisionPrompt("");
    };

    const startNew = () => {
        setDraftPoster(null);
        setAnswers(defaultAnswers(paperSize, focusElement));
        setPhase("form");
        setError("");
        setRevisionPrompt("");
    };

    const saveDraft = () => {
        if (!draftPoster) return;
        onGenerated({ ...draftPoster, ai_generated: true });
        onHide();
        toast(t("print.poster.ai.saved"), "success");
    };

    const questionnaire = (
        <>
            <p className="print-poster-ai-lead">{t("print.poster.ai.lead")}</p>
            <div className="print-poster-ai-grid">
                <DBFieldBlock
                    title={t("print.poster.ai.industry")}
                    required
                    error={error}
                >
                    <DBTextArea
                        rows={3}
                        value={answers.industry}
                        placeholder={t("print.poster.ai.industry.placeholder")}
                        onChange={(e) =>
                            setAnswers({
                                ...answers,
                                industry: e.target.value,
                            })
                        }
                    />
                </DBFieldBlock>
                <DBFieldBlock title={t("print.poster.ai.audience")}>
                    <DBTextArea
                        rows={2}
                        value={answers.audience}
                        placeholder={t("print.poster.ai.audience.placeholder")}
                        onChange={(e) =>
                            setAnswers({
                                ...answers,
                                audience: e.target.value,
                            })
                        }
                    />
                </DBFieldBlock>
                <DBFieldBlock title={t("print.poster.ai.tone")}>
                    <DBSelect
                        value={answers.tone}
                        options={toneOptions}
                        onChange={(value) =>
                            setAnswers({ ...answers, tone: value })
                        }
                        style={{ width: "100%" }}
                    />
                </DBFieldBlock>
                <DBFieldBlock title={t("print.poster.ai.focus")}>
                    <DBSelect
                        value={answers.focus_element}
                        options={POSTER_FOCUS_ELEMENTS.map((id) => ({
                            value: id,
                            label: t(`print.poster.focus.${id}`),
                        }))}
                        onChange={(value) =>
                            setAnswers({
                                ...answers,
                                focus_element: value,
                            })
                        }
                        style={{ width: "100%" }}
                    />
                </DBFieldBlock>
                <DBFieldBlock title={t("print.poster.paper")}>
                    <DBSelect
                        value={answers.paper_size}
                        options={paperOptions}
                        onChange={(value) =>
                            setAnswers({
                                ...answers,
                                paper_size: value,
                            })
                        }
                        style={{ width: "100%" }}
                    />
                </DBFieldBlock>
                <ModelField
                    value={selectedModel}
                    onChange={setSelectedModel}
                />
                <DBFieldBlock title={t("print.poster.ai.notes")} span={2}>
                    <DBTextArea
                        rows={3}
                        value={answers.notes}
                        placeholder={t("print.poster.ai.notes.placeholder")}
                        onChange={(e) =>
                            setAnswers({
                                ...answers,
                                notes: e.target.value,
                            })
                        }
                    />
                </DBFieldBlock>
            </div>
        </>
    );

    const preview = (
        <div className="print-poster-ai-preview">
            <p className="print-poster-ai-lead">
                {t("print.poster.ai.preview.lead")}
            </p>
            <div
                className={`print-poster-ai-preview-frame${
                    generateLoading ? " is-loading" : ""
                }`}
            >
                <PrintPosterPreview
                    poster={draftPoster}
                    company={company}
                    itemName={SAMPLE_ITEM_NAME}
                />
            </div>
            <ModelField
                value={selectedModel}
                onChange={setSelectedModel}
            />
            <DBFieldBlock title={t("print.poster.ai.revise")} error={error}>
                <DBTextArea
                    rows={3}
                    value={revisionPrompt}
                    placeholder={t("print.poster.ai.revise.placeholder")}
                    onChange={(e) => setRevisionPrompt(e.target.value)}
                />
            </DBFieldBlock>
        </div>
    );

    const choice = (
        <div className="print-poster-ai-choice">
            <p className="print-poster-ai-lead">
                {t("print.poster.ai.choice.lead")}
            </p>
            <button
                type="button"
                className="print-poster-ai-choice__option"
                onClick={startReviseExisting}
            >
                <strong>{t("print.poster.ai.choice.revise")}</strong>
                <span>{t("print.poster.ai.choice.revise.hint")}</span>
            </button>
            <button
                type="button"
                className="print-poster-ai-choice__option"
                onClick={startNew}
            >
                <strong>{t("print.poster.ai.choice.new")}</strong>
                <span>{t("print.poster.ai.choice.new.hint")}</span>
            </button>
        </div>
    );

    const questionnaireModal = (
        <DBModal
            show={show && !coinGateOpen}
            onHide={onHide}
            minWidth={isPreview ? 640 : 480}
            stacked
            className={`print-poster-ai-modal${
                isPreview ? " print-poster-ai-modal--preview" : ""
            }`}
            ariaLabelledby="print-poster-ai-title"
        >
            <DBModal.Header
                closeBtn={onHide}
                title={
                    <span id="print-poster-ai-title">
                        {t("print.poster.ai.title")}
                    </span>
                }
            />
            <DBModal.Content>
                {isChoice ? choice : isPreview ? preview : questionnaire}
            </DBModal.Content>
            <DBModal.Footer>
                <DBButton
                    type="secondary"
                    caption={t("cancel")}
                    onClick={onHide}
                />
                {isChoice ? null : isPreview ? (
                    <>
                        <DBButton
                            className="print-poster-designer__ai-button"
                            caption={
                                <>
                                    {t("print.poster.ai.revise.apply")}
                                    <AiCoinHint operation="print_poster" />
                                </>
                            }
                            loading={generateLoading}
                            onClick={revise}
                        />
                        <DBButton
                            caption={t("print.poster.ai.save")}
                            disabled={generateLoading}
                            onClick={saveDraft}
                        />
                    </>
                ) : (
                    <DBButton
                        className="print-poster-designer__ai-button"
                        caption={
                            <>
                                {t("print.poster.ai.generate")}
                                <AiCoinHint operation="print_poster" />
                            </>
                        }
                        loading={generateLoading}
                        onClick={generate}
                    />
                )}
            </DBModal.Footer>
        </DBModal>
    );

    const tree = (
        <>
            {typeof document === "undefined" ? (
                questionnaireModal
            ) : (
                createPortal(
                    <div className="print-poster-ai-layer">
                        {questionnaireModal}
                    </div>,
                    document.body
                )
            )}
            {gate}
        </>
    );

    return tree;
}
